/**
 * MCP: подключение Phygital+ к агенту.
 *
 * Адрес сервера приходит из рантайм-конфига (`/api/config` -> `mcp_url`), а не
 * зашит в бандл: SPA собирается один раз на все стенды, и у каждого свой хост.
 * Конфиги клиентов на странице строятся вокруг этого адреса — правится он в
 * одном месте, в окружении бэкенда портала. Оттуда же приходит `mcp_oauth_enabled`:
 * браузерный вход показывается только на стендах, где authorization server
 * действительно поднят, иначе инструкция вела бы клиента в несуществующий
 * discovery.
 *
 * Тексты — в словарях `i18n/ui/*.json` (ключи `mcp.*`). Здесь только структура и
 * то, что переводить не надо: имена инструментов, их аргументы и синтаксис
 * конфигов.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../app/routes";
import { usePortalConfig } from "../app/usePortalConfig";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Card,
  CodeBlock,
  Divider,
  InlineCode,
  Link,
  Table,
  Tabs,
  typographyClass,
} from "../ui-kit";

/** Инструменты сервера. Имена и аргументы — контракт, он одинаков во всех языках. */
const TOOLS: { name: string; args: string; descriptionKey: string }[] = [
  { name: "list_models", args: "category?", descriptionKey: "mcp.tool.listModels" },
  { name: "get_model_schema", args: "model", descriptionKey: "mcp.tool.getModelSchema" },
  { name: "generate", args: "model, input", descriptionKey: "mcp.tool.generate" },
  { name: "get_job", args: "jobId", descriptionKey: "mcp.tool.getJob" },
  { name: "cancel_job", args: "jobId", descriptionKey: "mcp.tool.cancelJob" },
  { name: "get_upload_url", args: "—", descriptionKey: "mcp.tool.getUploadUrl" },
  { name: "upload_file", args: "filename, mime_type, data_base64", descriptionKey: "mcp.tool.uploadFile" },
  { name: "start_upload", args: "filename, mime_type, total_bytes?", descriptionKey: "mcp.tool.startUpload" },
  { name: "upload_chunk", args: "session_id, index, data_base64", descriptionKey: "mcp.tool.uploadChunk" },
  { name: "finish_upload", args: "session_id", descriptionKey: "mcp.tool.finishUpload" },
  { name: "request_upload", args: "—", descriptionKey: "mcp.tool.requestUpload" },
  { name: "claim_upload", args: "code", descriptionKey: "mcp.tool.claimUpload" },
];

/** Права, которые клиент запрашивает в OAuth-флоу; их же человек видит на экране согласия. */
const SCOPES: { scope: string; descriptionKey: string }[] = [
  { scope: "tasks:read", descriptionKey: "mcp.scope.tasksRead" },
  { scope: "tasks:write", descriptionKey: "mcp.scope.tasksWrite" },
  { scope: "files:write", descriptionKey: "mcp.scope.filesWrite" },
  { scope: "projects:read", descriptionKey: "mcp.scope.projectsRead" },
  { scope: "frames:run", descriptionKey: "mcp.scope.framesRun" },
  { scope: "offline_access", descriptionKey: "mcp.scope.offlineAccess" },
];

/** Статусы задачи, как их видит агент: сырые статусы бэка сведены к четырём. */
const JOB_STATUSES: { status: string; raw: string }[] = [
  { status: "queued", raw: "new, preparing, waiting, waiting_for_launch, pending" },
  { status: "running", raw: "running" },
  { status: "completed", raw: "done" },
  { status: "canceled", raw: "canceled" },
];

/** Ответы, которые агент увидит как ошибку инструмента. */
const ERRORS: { code: string; reasonKey: string }[] = [
  { code: "401", reasonKey: "mcp.error.401" },
  { code: "402", reasonKey: "mcp.error.402" },
  { code: "422", reasonKey: "mcp.error.422" },
  { code: "429", reasonKey: "mcp.error.429" },
];

type Client = "claude" | "cursor" | "codex";

/**
 * Способ авторизации. Их два, и они не «старый и новый»: OAuth — там, где есть
 * браузер, ключ — там, где редиректу некуда прийти (curl, CI, серверные
 * интеграции). Сервер принимает оба одновременно.
 */
type AuthMode = "oauth" | "key";

const CLIENT_LABELS: Record<Client, string> = {
  claude: "Claude Code",
  cursor: "Cursor",
  codex: "Codex",
};

/**
 * Конфиг клиента строится вокруг адреса из рантайм-конфига.
 *
 * В варианте с OAuth заголовка нет вообще: клиент получает от сервера 401 с
 * `WWW-Authenticate`, сам находит authorization server и открывает браузер.
 * Дописанный «на всякий случай» Authorization этот флоу как раз и отменяет —
 * сервер примет предъявленный ключ, и никакого входа не случится.
 */
function clientSetup(client: Client, url: string, mode: AuthMode): { language: string; code: string } {
  const oauth = mode === "oauth";
  switch (client) {
    case "claude":
      return {
        language: "bash",
        code: oauth
          ? `claude mcp add --transport http phygital ${url}

# then run /mcp in Claude Code and choose Authenticate - the browser opens`
          : `claude mcp add --transport http phygital ${url} \\
  --header "Authorization: Bearer $PHYGITAL_API_KEY"`,
      };
    case "cursor":
      return {
        language: "json",
        code: oauth
          ? `// ~/.cursor/mcp.json
{
  "mcpServers": {
    "phygital": { "url": "${url}" }
  }
}

// Settings -> MCP: the server shows "Needs login", click it and sign in`
          : `// ~/.cursor/mcp.json
{
  "mcpServers": {
    "phygital": {
      "url": "${url}",
      "headers": { "Authorization": "Bearer pk_…" }
    }
  }
}`,
      };
    case "codex":
      return {
        language: "text",
        code: oauth
          ? `Settings → MCP servers → Add server
Transport:  Streamable HTTP
URL:        ${url}
Auth:       OAuth — the client opens the browser itself`
          : `Settings → MCP servers → Add server
Transport:  Streamable HTTP
URL:        ${url}
Header:     Authorization: Bearer pk_…`,
      };
  }
}

const SECTION_TEXT = { color: "var(--color-text-secondary-v2)", maxWidth: "68ch" } as const;

export function McpPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client>("claude");
  const config = usePortalConfig();
  const url = config?.mcp_url ?? null;
  const oauthEnabled = config?.mcp_oauth_enabled === true;
  // Выбор пользователя, пока его нет — способ по умолчанию для этого стенда.
  // Конфиг приезжает асинхронно, поэтому дефолт вычисляется, а не кладётся в
  // состояние: иначе первый рендер зафиксировал бы «ключ» навсегда.
  const [mode, setMode] = useState<AuthMode | null>(null);
  const authMode: AuthMode = mode ?? (oauthEnabled ? "oauth" : "key");

  const text = (key: string) => (
    <p className={typographyClass("text-14-medium")} style={SECTION_TEXT}>
      {t(key)}
    </p>
  );

  return (
    <Card>
      <h1 className={typographyClass("header-24-semibold")}>{t("mcp.title")}</h1>
      {text("mcp.subtitle")}

      {/* ------------------------------------------------------------ сервер */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.server.title")}</h2>
      {url === null ? (
        // Адрес задаётся окружением бэкенда: без него подключаться некуда, и
        // показывать инструкцию с пустым URL хуже, чем честно сказать об этом.
        <AlertBanner tone="warning" title={t("mcp.server.missing.title")}>
          {t("mcp.server.missing.text")}
        </AlertBanner>
      ) : (
        <Table
          rows={[
            { key: "url", label: t("mcp.server.url"), value: url },
            { key: "transport", label: t("mcp.server.transport"), value: "Streamable HTTP" },
            {
              // Значение — код, а не текст: переводить нечего, меняется только
              // набор способов, которые сервер принимает на этом стенде.
              key: "auth",
              label: t("mcp.server.auth"),
              value: oauthEnabled
                ? "OAuth / Authorization: Bearer <api key>"
                : "Authorization: Bearer <api key>",
            },
            { key: "path", label: t("mcp.server.mounted"), value: t("mcp.server.mounted.value") },
          ]}
          rowKey={(row) => row.key}
          columns={[
            { key: "label", header: t("mcp.server.parameter"), render: (row) => row.label },
            {
              key: "value",
              header: t("mcp.server.value"),
              render: (row) => <code className={typographyClass("mono-12")}>{row.value}</code>,
            },
          ]}
        />
      )}

      <Divider orientation="horizontal" />

      {/* -------------------------------------------------------- инструменты */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.tools.title")}</h2>
      {text("mcp.tools.subtitle")}

      <Table
        rows={TOOLS}
        rowKey={(row) => row.name}
        columns={[
          {
            key: "name",
            header: t("mcp.tools.tool"),
            render: (row) => <code className={typographyClass("mono-12")}>{row.name}</code>,
          },
          {
            key: "args",
            header: t("mcp.tools.args"),
            render: (row) => (
              <code className={typographyClass("mono-11")} style={{ color: "var(--color-text-secondary)" }}>
                {row.args}
              </code>
            ),
          },
          { key: "description", header: t("mcp.tools.what"), render: (row) => t(row.descriptionKey) },
        ]}
      />

      <Divider orientation="horizontal" />

      {/* --------------------------------------------------------- подключение */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.setup.title")}</h2>
      {text("mcp.setup.text")}

      {oauthEnabled && (
        <>
          {text("mcp.setup.modes")}
          <Tabs<AuthMode>
            value={authMode}
            onChange={setMode}
            variant="secondary"
            items={[
              { value: "oauth", label: t("mcp.setup.mode.oauth") },
              { value: "key", label: t("mcp.setup.mode.key") },
            ]}
          />
        </>
      )}

      <Tabs<Client>
        value={client}
        onChange={setClient}
        items={(Object.keys(CLIENT_LABELS) as Client[]).map((key) => ({
          value: key,
          label: CLIENT_LABELS[key],
        }))}
      />
      <CodeBlock
        {...clientSetup(client, url ?? "https://<mcp-host>/mcp", authMode)}
        copyLabel={t("action.copy")}
      />
      {text(authMode === "oauth" ? "mcp.setup.oauth.note" : "mcp.setup.key.note")}

      <Divider orientation="horizontal" />

      {/* --------------------------------------------------------------- ключ */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.auth.title")}</h2>
      {text(oauthEnabled ? "mcp.auth.text" : "mcp.auth.textKeyOnly")}

      {oauthEnabled && (
        <>
          <h3 className={typographyClass("text-14-semibold")}>{t("mcp.auth.oauth.title")}</h3>
          {text("mcp.auth.oauth.text")}
          {text("mcp.auth.oauth.session")}
          <Table
            rows={SCOPES}
            rowKey={(row) => row.scope}
            columns={[
              {
                key: "scope",
                header: t("mcp.auth.scopes.scope"),
                render: (row) => <code className={typographyClass("mono-12")}>{row.scope}</code>,
              },
              {
                key: "description",
                header: t("mcp.auth.scopes.what"),
                render: (row) => t(row.descriptionKey),
              },
            ]}
          />
        </>
      )}

      <h3 className={typographyClass("text-14-semibold")}>{t("mcp.auth.key.title")}</h3>
      {text("mcp.auth.key.text")}
      {text("mcp.auth.scope")}
      {config?.api_keys_url && (
        <Link href={config.api_keys_url} target="_blank">
          {t("auth.getKey")} →
        </Link>
      )}

      <Divider orientation="horizontal" />

      {/* ---------------------------------------------------------------- флоу */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.flow.title")}</h2>
      {text("mcp.flow.text")}
      <CodeBlock
        // Аннотации по-английски при любом языке интерфейса: это код, а код в
        // проекте всегда английский — переводить его в словарях смысла нет.
        code={`1. list_models(category="video")        → models and their slugs
2. get_model_schema(model="kling")     → JSON Schema of inputs and params
3. generate(model="kling", input={     → task created, jobId returned
     "text_prompt": "slow dolly in",
     "init_img": [501],
     "duration": "sec_5"
   })
4. get_job(jobId)                      → queued → running → completed + links`}
        copyLabel={t("action.copy")}
      />

      <h3 className={typographyClass("text-14-semibold")}>{t("mcp.upload.title")}</h3>
      {text("mcp.upload.text")}
      <CodeBlock
        code={`// файл у агента
upload_file("shot.jpg", "image/jpeg", "<base64>")  → { "storage_object_id": 501 }

// файл у агента, но в один вызов не влезает
start_upload("clip.mp4", "video/mp4")   → { "session_id": "…", "chunk_max_bytes": 2097152 }
upload_chunk(session_id, 0, "<base64>") → { "next_index": 1 }
upload_chunk(session_id, 1, "<base64>") → { "next_index": 2 }
finish_upload(session_id)               → { "storage_object_id": 502 }

// файл у агента, но крупный, и свой HTTP есть
get_upload_url()  → { "upload_url": "…/api/uploads", "file_field": "file" }
curl -X POST "$upload_url" -H "Authorization: Bearer $KEY" -F file=@shot.jpg
                  → { "storage_object_id": 501 }

// файл у человека — панель загрузки прямо в чате
request_upload()  → { "status": "waiting_for_the_person", "ticket": "…" }
                    панель; человек кладёт файл → код
claim_upload(code) → { "storage_object_id": 501 }`}
        copyLabel={t("action.copy")}
      />

      <h3 className={typographyClass("text-14-semibold")}>{t("mcp.statuses.title")}</h3>
      <Table
        rows={JOB_STATUSES}
        rowKey={(row) => row.status}
        columns={[
          {
            key: "status",
            header: t("mcp.statuses.status"),
            render: (row) => <code className={typographyClass("mono-12")}>{row.status}</code>,
          },
          {
            key: "raw",
            header: t("mcp.statuses.raw"),
            render: (row) => (
              <code className={typographyClass("mono-11")} style={{ color: "var(--color-text-secondary)" }}>
                {row.raw}
              </code>
            ),
          },
        ]}
      />

      <p className={typographyClass("text-14-medium")} style={SECTION_TEXT}>
        {t("mcp.credits")} <InlineCode>cancel_job</InlineCode> {t("mcp.credits.tail")}
      </p>

      <Divider orientation="horizontal" />

      {/* -------------------------------------------------------------- ошибки */}
      <h2 className={typographyClass("header-16-semibold")}>{t("mcp.errors.title")}</h2>
      <Table
        rows={ERRORS}
        rowKey={(row) => row.code}
        columns={[
          {
            key: "code",
            header: t("mcp.errors.code"),
            render: (row) => <code className={typographyClass("mono-12")}>{row.code}</code>,
          },
          { key: "reason", header: t("mcp.errors.reason"), render: (row) => t(row.reasonKey) },
        ]}
      />
      {oauthEnabled && text("mcp.errors.scope")}

      <Divider orientation="horizontal" />

      {/* -------------------------------------------------------------- навыки */}
      {text("mcp.skillsPromo.text")}
      <Link onClick={() => navigate(ROUTES.skills)}>{t("mcp.skillsPromo.link")} →</Link>
    </Card>
  );
}
